<template>
    <div class="image-selection-box">
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Selected Images</h6>
            <button 
                v-if="allowAdd && canEdit('playbooks')" 
                class="btn btn-sm btn-primary" 
                @click="openImageLibrary"
                :disabled="loading"
            >
                <img src="/img/icons/avid-icons/plus.svg" class="icon me-1" alt=""> Add Image
            </button>
        </div>
        
        <div v-if="images.length > 0" class="row g-2">
            <div v-for="image in images" :key="image.id" class="col-md-3 col-sm-4 col-6">
                <div class="card image-card position-relative">
                    <div class="image-preview">
                        <img 
                            v-if="getImageUrl(image)" 
                            :src="getImageUrl(image)" 
                            :alt="image.name"
                            class="card-img-top"
                            style="height: 150px; object-fit: cover;"
                        >
                        <div v-else class="d-flex align-items-center justify-content-center" style="height: 150px; background: #f0f0f0;">
                            <span class="text-muted small">No preview</span>
                        </div>
                        <div class="position-absolute top-0 end-0 m-2">
                            <span :class="['badge', getClassificationBadgeClass(image.image_classification)]">
                                {{ formatClassification(image.image_classification) }}
                            </span>
                        </div>
                        <button 
                            v-if="allowRemove && canEdit('playbooks')" 
                            class="btn btn-sm btn-white position-absolute top-0 start-0 m-2" 
                            @click="removeImage(image)"
                            data-bs-toggle="tooltip"
                            title="Remove image"
                        >
                            <img src="/img/icons/avid-icons/trash.svg" alt="Remove" class="icon red-icon">
                        </button>
                    </div>
                    <div class="card-body p-2">
                        <h6 class="card-title mb-0 text-truncate" :title="image.name">{{ image.name }}</h6>
                        <p v-if="image.description" class="card-text text-muted small mb-0 mt-1" style="font-size: 0.75rem;">
                            {{ truncate(image.description, 40) }}
                        </p>
                    </div>
                </div>
            </div>
        </div>
        <div v-else class="text-muted fst-italic">
            No images selected yet.
        </div>
    </div>

    <!-- Image Library Modal -->
    <div class="modal fade" id="imageLibraryModal" tabindex="-1" aria-labelledby="imageLibraryModalLabel" aria-hidden="true">
        <div class="modal-dialog modal-lg">
            <div class="modal-content">
                <div class="modal-header border-bottom-0 pb-0">
                    <div class="w-100 position-relative">
                        <button type="button" class="btn btn-white position-absolute top-0 end-0 close-modal" data-bs-dismiss="modal" style="z-index: 10;" data-bs-toggle="tooltip" title="Close">
                            <img class="icon me-0" src="/img/icons/avid-icons/close.svg" alt=""> 
                        </button>
                        <h3 class="mt-2 pe-5">Select Images</h3>
                    </div>
                </div>
                <div class="modal-body">
                    <div v-if="loadingLibrary" class="text-center">
                        <div class="spinner-border" role="status">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <p class="mt-2">Loading images...</p>
                    </div>
                    <div v-else>
                        <div class="mb-3">
                            <input 
                                type="text" 
                                class="form-control" 
                                placeholder="Search images..." 
                                v-model="searchQuery"
                                @input="searchImages"
                             aria-label="Search images...">
                        </div>
                        <div class="row g-2" style="max-height: 400px; overflow-y: auto;">
                            <div v-for="image in availableImages" :key="image.id" class="col-md-3 col-sm-4 col-6">
                                <div 
                                    class="card image-card-selectable" 
                                    :class="{ 'selected': isImageSelected(image.id) }"
                                    @click="toggleImageSelection(image)"
                                    style="cursor: pointer;"
                                >
                                    <div class="image-preview position-relative">
                                        <img 
                                            v-if="getImageUrl(image)" 
                                            :src="getImageUrl(image)" 
                                            :alt="image.name"
                                            class="card-img-top"
                                            style="height: 120px; object-fit: cover;"
                                        >
                                        <div v-else class="d-flex align-items-center justify-content-center" style="height: 120px; background: #f0f0f0;">
                                            <span class="text-muted small">No preview</span>
                                        </div>
                                        <div v-if="isImageSelected(image.id)" class="position-absolute top-0 end-0 m-2">
                                            <span class="badge bg-success">Selected</span>
                                        </div>
                                    </div>
                                    <div class="card-body p-2">
                                        <h6 class="card-title mb-0 text-truncate" :title="image.name">{{ image.name }}</h6>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
                    <button 
                        type="button" 
                        class="btn btn-primary" 
                        @click="confirmImageSelection"
                        :disabled="selectedImageIds.length === 0"
                    >
                        Add Selected ({{ selectedImageIds.length }})
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { defineComponent } from 'vue';
import permissionsMixin from '../../mixins/permissions';
import debounce from 'lodash.debounce';

export default defineComponent({
    name: 'ImageSelectionBox',
    mixins: [permissionsMixin],
    props: {
        images: {
            type: Array,
            default: () => []
        },
        allowAdd: {
            type: Boolean,
            default: true
        },
        allowRemove: {
            type: Boolean,
            default: true
        },
        showClassification: {
            type: Boolean,
            default: true
        },
        client: {
            type: Object,
            required: true
        },
        maxImages: {
            type: Number,
            default: 3
        }
    },
    emits: ['add-image', 'remove-image'],
    data() {
        return {
            loading: false,
            loadingLibrary: false,
            availableImages: [],
            searchQuery: '',
            selectedImageIds: [],
        };
    },
    mounted() {
        this.debouncedSearch = debounce(this.searchImages, 500);
        // Ensure modal is hidden on mount and clean up any Bootstrap modal artifacts
        this.$nextTick(() => {
            this.ensureModalHidden();
            // Add event listener to clean up when modal is hidden
            const modalElement = document.getElementById('imageLibraryModal');
            if (modalElement) {
                modalElement.addEventListener('hidden.bs.modal', () => {
                    this.ensureModalHidden();
                });
            }
        });
    },
    beforeUnmount() {
        // Clean up modal when component is destroyed
        this.ensureModalHidden();
    },
    methods: {
        ensureModalHidden() {
            const modalElement = document.getElementById('imageLibraryModal');
            if (modalElement) {
                const modal = bootstrap.Modal.getInstance(modalElement);
                if (modal) {
                    modal.hide();
                }
                // Remove any existing backdrop
                const backdrop = document.querySelector('.modal-backdrop');
                if (backdrop) {
                    backdrop.remove();
                }
                // Remove body class that Bootstrap adds when modal is open
                document.body.classList.remove('modal-open');
                document.body.style.overflow = '';
                document.body.style.paddingRight = '';
                // Ensure modal doesn't have show class
                modalElement.classList.remove('show');
                modalElement.setAttribute('aria-hidden', 'true');
                modalElement.style.display = 'none';
            }
        },
        openImageLibrary() {
            this.selectedImageIds = [];
            this.searchQuery = '';
            this.loadImageLibrary();
            const modalElement = document.getElementById('imageLibraryModal');
            const modal = new bootstrap.Modal(modalElement);
            modal.show();
        },
        
        async loadImageLibrary() {
            this.loadingLibrary = true;
            try {
                const params = new URLSearchParams({
                    search: this.searchQuery || '',
                    per_page: 50,
                    page: 1,
                });
                
                const response = await window.axios.get(`/api/client/${this.client.url}/images?${params}`);
                this.availableImages = response.data.images || [];
            } catch (error) {
                console.error('Error loading images:', error);
                this.availableImages = [];
            } finally {
                this.loadingLibrary = false;
            }
        },
        
        searchImages() {
            this.loadImageLibrary();
        },
        
        toggleImageSelection(image) {
            const index = this.selectedImageIds.indexOf(image.id);
            if (index > -1) {
                this.selectedImageIds.splice(index, 1);
            } else {
                if (this.selectedImageIds.length < this.maxImages) {
                    this.selectedImageIds.push(image.id);
                }
            }
        },
        
        isImageSelected(imageId) {
            return this.selectedImageIds.includes(imageId);
        },
        
        confirmImageSelection() {
            const selectedImages = this.availableImages.filter(img => this.selectedImageIds.includes(img.id));
            selectedImages.forEach(image => {
                this.$emit('add-image', image);
            });
            const modalElement = document.getElementById('imageLibraryModal');
            const modal = bootstrap.Modal.getInstance(modalElement);
            if (modal) {
                modal.hide();
            }
            // Ensure cleanup after hiding
            this.$nextTick(() => {
                this.ensureModalHidden();
            });
        },
        
        removeImage(image) {
            this.$emit('remove-image', image);
        },
        
        getImageUrl(image) {
            // Return GCS path if available
            return image.gcs_path || '';
        },
        
        getClassificationBadgeClass(classification) {
            const classes = {
                'mission_in_action': 'bg-primary',
                'location': 'bg-info',
                'products': 'bg-warning',
                'logo': 'bg-success',
                'design_styles': 'bg-secondary',
            };
            return classes[classification] || 'bg-secondary';
        },
        
        formatClassification(classification) {
            return classification.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
        },
        
        truncate(str, length) {
            if (!str) return '';
            return str.length > length ? str.substring(0, length) + '...' : str;
        },
    }
});
</script>

<style scoped>
.image-card {
    transition: transform 0.2s;
}

.image-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.image-card-selectable {
    transition: all 0.2s;
}

.image-card-selectable:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
}

.image-card-selectable.selected {
    border: 2px solid #0d6efd;
}
</style>

